import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/**
 * PrimeUI (Aura) preset wired to the Spyglass palette.
 *
 * Palette CSS variables store full rgb() values, so no wrapping needed:
 *   --ap-primary-500: rgb(50, 136, 125);
 *   usage: var(--ap-primary-500)
 */
const PrimengPreset = definePreset(Aura, {
  primitive: {
    green: {
      50: 'var(--ap-primary-50)',
      100: 'var(--ap-primary-100)',
      200: 'var(--ap-primary-200)',
      300: 'var(--ap-primary-300)',
      400: 'var(--ap-primary-400)',
      500: 'var(--ap-primary-500)',
      600: 'var(--ap-primary-600)',
      700: 'var(--ap-primary-700)',
      800: 'var(--ap-primary-800)',
      900: 'var(--ap-primary-900)',
      950: 'var(--ap-primary-950)',
    },
    orange: {
      50: 'var(--ap-secondary-50)',
      100: 'var(--ap-secondary-100)',
      200: 'var(--ap-secondary-200)',
      300: 'var(--ap-secondary-300)',
      400: 'var(--ap-secondary-400)',
      500: 'var(--ap-secondary-500)',
      600: 'var(--ap-secondary-600)',
      700: 'var(--ap-secondary-700)',
      800: 'var(--ap-secondary-800)',
      900: 'var(--ap-secondary-900)',
      950: 'var(--ap-secondary-950)',
    },
    red: {
      50: 'var(--ap-danger-50)',
      100: 'var(--ap-danger-100)',
      200: 'var(--ap-danger-200)',
      300: 'var(--ap-danger-300)',
      400: 'var(--ap-danger-400)',
      500: 'var(--ap-danger-500)',
      600: 'var(--ap-danger-600)',
      700: 'var(--ap-danger-700)',
      800: 'var(--ap-danger-800)',
      900: 'var(--ap-danger-900)',
      950: 'var(--ap-danger-950)',
    },
    neutral: {
      0: 'var(--ap-neutral-0)',
      50: 'var(--ap-neutral-50)',
      100: 'var(--ap-neutral-100)',
      200: 'var(--ap-neutral-200)',
      300: 'var(--ap-neutral-300)',
      400: 'var(--ap-neutral-400)',
      500: 'var(--ap-neutral-500)',
      600: 'var(--ap-neutral-600)',
      700: 'var(--ap-neutral-700)',
      800: 'var(--ap-neutral-800)',
      900: 'var(--ap-neutral-900)',
      950: 'var(--ap-neutral-950)',
    },
  },
  semantic: {
    primary: {
      50: '{green.50}',
      100: '{green.100}',
      200: '{green.200}',
      300: '{green.300}',
      400: '{green.400}',
      500: '{green.500}',
      600: '{green.600}',
      700: '{green.700}',
      800: '{green.800}',
      900: '{green.900}',
      950: '{green.950}',
    },
    border: {
      subtle: 'color-mix(in srgb, {neutral.950} 10%, transparent)',
      muted: '{neutral.100}',
      default: '{neutral.200}',
      brand: '{primary.100}',
      'brand-strong': '{primary.500}',
      secondary: '{orange.500}',
      'secondary-accent': '{orange.600}',
      danger: '{red.500}',
    },
    shadow: {
      soft: 'color-mix(in srgb, {primary.700} 22%, transparent)',
      heavy: 'color-mix(in srgb, {primary.700} 45%, transparent)',
      'brand-color': 'color-mix(in srgb, {primary.700} 18%, transparent)',
      'muted-xs': '0 0 0.5rem {border.subtle}',
      'muted-sm': '0 0.5rem 1rem -0.5rem {shadow.soft}',
      'muted-md':
        '0 0.75rem 1.75rem -0.75rem {shadow.heavy}, 0 0.25rem 0.75rem -0.25rem {shadow.soft}',
      'muted-lg':
        '0 1.125rem 2.5rem -2.125rem {shadow.heavy}, 0 0.375rem 1.125rem -0.875rem {shadow.soft}',
      'muted-xl':
        '0 1.25rem 2.75rem -2.125rem {shadow.heavy}, 0 0.625rem 1.375rem -1.125rem {shadow.soft}',
      'brand-xs': '0 0 0.5rem {shadow.brand-color}',
      'brand-sm': '0 0.5rem 1rem -0.5rem {shadow.brand-color}',
      'brand-md':
        '0 0.75rem 1.75rem -0.75rem {shadow.brand-color}, 0 0.25rem 0.75rem -0.25rem {shadow.brand-color}',
      'brand-lg':
        '0 1.125rem 2.5rem -2.125rem {shadow.brand-color}, 0 0.375rem 1.125rem -0.875rem {shadow.brand-color}',
      'brand-xl':
        '0 1.25rem 2.75rem -2.125rem {shadow.brand-color}, 0 0.625rem 1.375rem -1.125rem {shadow.brand-color}',
    },
    colorScheme: {
      light: {
        primary: {
          color: '{primary.500}',
          contrastColor: '{neutral.0}',
          hoverColor: '{primary.700}',
          activeColor: '{primary.800}',
        },
        content: {
          color: '{neutral.700}',
        },
        highlight: {
          background: 'color-mix(in srgb, {primary.500} 8%, transparent)',
          focusBackground: 'color-mix(in srgb, {primary.500} 8%, transparent)',
          color: '{neutral.700}',
          focusColor: '{neutral.800}',
        },
        surface: {
          0: '{neutral.0}',
          50: '{neutral.50}',
          100: '{neutral.100}',
          200: '{neutral.200}',
          300: '{neutral.300}',
          400: '{neutral.400}',
          500: '{neutral.500}',
          600: '{neutral.600}',
          700: '{neutral.700}',
          800: '{neutral.800}',
          900: '{neutral.900}',
          950: '{neutral.950}',
        },
        formField: {
          background: '{surface.0}',
          disabledBackground: '{surface.50}',
          borderColor: '{border.default}',
          hoverBorderColor: '{border.muted}',
          focusBorderColor: '{primary.500}',
          color: '{neutral.700}',
          disabledColor: '{surface.300}',
          placeholderColor: '{surface.600}',
          invalidBorderColor: '{red.500}',
        },
      },
    },
  },
  /**
   * Design-token overrides (no global CSS needed).
   *
   * These mirror `ButtonComponent` styles so PrimeNG buttons
   * can be used directly (via `severity="primary" | "secondary"`).
   */
  components: {
    inputtext: {
      css: () => `
        .p-inputtext {
          width: 100%;
        }
      `,
    },
    password: {
      css: () => `
        .p-password {
          width: 100%;
        }
      `,
    },
    datepicker: {
      css: () => `
        .p-datepicker-input {
          padding-right: 2.5rem !important;
        }
      `,
    },
    paginator: {
      root: {
        background: '{surface.0}',
        borderRadius: '0.75rem',
        padding: '0.75rem 1rem',
        color: '{neutral.800}',
      },
      navButton: {
        background: 'transparent',
        hoverBackground: '{surface.50}',
        selectedBackground: '{primary.700}',
        color: '{neutral.800}',
        hoverColor: '{neutral.800}',
        selectedColor: '{neutral.0}',
        width: '2rem',
        height: '2rem',
        borderRadius: '0.375rem',
        focusRing: {
          width: '2px',
          style: 'solid',
          color: '{primary.500}',
          offset: '2px',
          shadow: 'none',
        },
      },
      currentPageReport: {
        color: '{neutral.800}',
      },
      css: () => `
        .p-paginator {
          margin-top: 0.625rem;
          font-size: 0.875rem;
          box-shadow:
            0 1px 3px 0 var(--primeng-border-subtle),
            0 1px 2px -1px var(--primeng-border-subtle);
        }

        .p-paginator .p-paginator-pages .p-paginator-page,
        .p-paginator .p-paginator-first,
        .p-paginator .p-paginator-prev,
        .p-paginator .p-paginator-next,
        .p-paginator .p-paginator-last {
          border: 1px solid transparent !important;
          transition: all 0.15s ease !important;
        }

        .p-paginator .p-paginator-pages .p-paginator-page.p-paginator-page-selected {
          border-color: var(--ap-primary-700) !important;
          font-weight: 600 !important;
        }

        .p-paginator .p-paginator-pages .p-paginator-page.p-paginator-page-selected:hover {
          background-color: var(--ap-primary-800) !important;
          border-color: var(--ap-primary-800) !important;
        }

        .p-paginator .p-paginator-first.p-disabled,
        .p-paginator .p-paginator-prev.p-disabled,
        .p-paginator .p-paginator-next.p-disabled,
        .p-paginator .p-paginator-last.p-disabled {
          opacity: 0.4 !important;
          cursor: not-allowed !important;
        }

        .p-paginator .p-paginator-rpp-dropdown {
          border: 1px solid var(--primeng-border-default) !important;
          min-height: 2rem !important;
        }

        .p-paginator .p-paginator-rpp-dropdown:hover {
          border-color: var(--text-muted) !important;
        }

        .p-paginator .p-paginator-rpp-dropdown:focus {
          outline: 2px solid var(--link-color) !important;
          outline-offset: 2px !important;
          border-color: var(--link-color) !important;
        }

        .p-paginator .p-paginator-rpp-dropdown .p-select-label {
          padding: 0.25rem 0.5rem !important;
        }
      `,
    },

    tooltip: {
      root: {
        maxWidth: '100%',
        gutter: '0',
        padding: '0.35rem 0.5rem',
        shadow: 'none',
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            color: '{neutral.900}',
          },
        },
      },
      css: () => `
        .p-tooltip .p-tooltip-text {
          font-size: 0.75rem;
          width: 100%;
          height: 100%;
          border: 1px solid var(--primeng-border-default);
        }

        .p-tooltip .p-tooltip-arrow {
          display: none;
        }
      `,
    },
    button: {
      root: {
        gap: '0.5rem',
        borderRadius: '0.75rem',
        roundedBorderRadius: '0.75rem',
        paddingX: '1.25rem',
        paddingY: '0.6rem',
        label: {
          fontWeight: 'var(--ap-font-weight-normal)',
        },
        raisedShadow: 'none',
        // Avoid first-render morphing when lazy route/theme tokens settle.
        transitionDuration: '0s',
        // Button default sizing: PrimeUI uses sm/lg for font size.
        sm: {
          fontSize: '0.875rem',
          paddingX: '1.25rem',
          paddingY: '0.6rem',
        },
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              // Matches `.primary-button` (neutral background + teal border).
              background: '{surface.50}',
              hoverBackground: '{surface.600}',
              activeBackground: '{surface.800}',
              borderColor: '{border.default}',
              hoverBorderColor: '{border.default}',
              activeBorderColor: '{border.default}',
              color: '{neutral.700}',
              hoverColor: '{primary.contrast.color}',
              activeColor: '{primary.contrast.color}',
              focusRing: {
                // Single source of truth: semantic primary.
                color: '{primary.color}',
                shadow: 'none',
              },
            },
            secondary: {
              // Secondary severity mapped to overridden orange primitive scale.
              background: '{orange.500}',
              hoverBackground: '{orange.600}',
              activeBackground: '{orange.700}',
              borderColor: 'transparent',
              hoverBorderColor: 'transparent',
              activeBorderColor: 'transparent',
              color: '{neutral.0}',
              hoverColor: '{neutral.0}',
              activeColor: '{neutral.0}',
              focusRing: {
                color: '{orange.500}',
                shadow: 'none',
              },
            },
            danger: {
              // Danger severity mapped to overridden red primitive scale.
              background: '{red.500}',
              hoverBackground: '{red.600}',
              activeBackground: '{red.700}',
              borderColor: '{red.500}',
              hoverBorderColor: '{red.600}',
              activeBorderColor: '{red.700}',
              color: '{neutral.0}',
              hoverColor: '{neutral.0}',
              activeColor: '{neutral.0}',
              focusRing: {
                color: '{red.500}',
                shadow: 'none',
              },
            },
            contrast: {
              // Reused as app tertiary variant.
              background: '{surface.0}',
              hoverBackground: '{surface.50}',
              activeBackground: '{surface.100}',
              borderColor: '{border.default}',
              hoverBorderColor: '{border.default}',
              activeBorderColor: '{primary.200}',
              color: '{neutral.700}',
              hoverColor: '{neutral.700}',
              activeColor: '{neutral.800}',
              focusRing: {
                color: '{primary.500}',
                shadow: 'none',
              },
            },
          },
        },
      },
    },
  },
});

export default PrimengPreset;
